import { API } from '@aws-amplify/api';
import { Role } from '../../@types/auth';
import { CognitoUser, UserListUser } from '../../@types/user';
import getRole from '../getRole';

// Safety valve only. The backend accumulates >= 1500 users per response, so the
// pool would have to exceed ~75k identities to reach this. It exists so a bug in
// the backend's token handling cannot spin the browser forever.
const MAX_PAGES_TO_FETCH = 50;

export async function getUserList(): Promise<UserListUser[]> {
  try {
    const apiName = process.env.NEXT_PUBLIC_DASH_API_NAME!;
    const path = 'get-user-list';
    let token: string | undefined = undefined;
    const users: CognitoUser[] = [];
    let pages = 0;
    // Continue purely on the presence of a continuation token. The previous
    // condition stopped unless a response held exactly 1500 users, but the
    // backend fills its batch to *at least* 1500 and returns whatever the final
    // Cognito page pushed it to (1501-1559 in practice), so the loop exited on
    // the first response and silently dropped every later user.
    do {
      const response: { users: CognitoUser[]; nextToken: string | undefined } = await API.get(
        apiName,
        path,
        { queryStringParameters: { nextToken: token } }
      );
      users.push(...response.users);
      token = response.nextToken;
      pages += 1;
    } while (token && pages < MAX_PAGES_TO_FETCH);
    // Convert CognitoUser to UserListUser
    const returnUsers = users.map((user: CognitoUser) => {
      const group = user.groups?.length > 0 ? user.groups[0] : 0;
      const outputUser: UserListUser = {
        name: user.name,
        email: user.email,
        image: user.image,
        verified: user.verified,
        group,
        level: user.level,
        campusId: user.campusId,
        role: getRole(group?.toString()),
        studentId: undefined,
        studentIds: undefined,
        // Cognito's Enabled flag. Absent means enabled, matching Cognito's own
        // behaviour of omitting it for active accounts.
        enabled: user.enabled ?? true,
      };

      if (user.studentIds.length > 0) {
        outputUser.studentIds = user.studentIds;

        if (outputUser.role === Role.STUDENT && user.studentIds[0] !== '%') {
          outputUser.studentId = user.studentIds[0];
        }
      }

      return outputUser;
    });

    return returnUsers;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
